import React, { useState, useEffect, useRef } from "react";
import BookingSteps from "./booking/BookingSteps";
import { useBookingFlow } from "@/hooks/useBookingFlow";
import { useStepHistory } from "@/hooks/useStepHistory";
import BookingHeader from "./booking/BookingHeader";
import Footer from "./booking/Footer";
import FAQ from "./FAQ";
import { EmbeddedPaymentModal } from "./booking-form/EmbeddedPaymentModal";
import PersistentDesktopSidebar from "./booking/PersistentDesktopSidebar";
import { AdvisoryBoard } from "./product-step/AdvisoryBoard";
import SessionConflictDialog from "./SessionConflictDialog";
import { useMembershipPlanStore } from "@/stores/membershipPlanStore";
import { useMarket } from "@/hooks/useMarket";
import { useFeatureFlags } from "@/hooks/useFeatureFlags";
import { marketHasAdvisoryBoard, marketHasMembership } from "@/config/market";

const BookingCalendar = () => {
  const bookingFlow = useBookingFlow();

  // Membership cadence is a focused page — hide the marketing FAQ + experts.
  // Gated to the CH market behind the membership_v2 flag (#342 market logic).
  const { market } = useMarket();
  const { data: featureFlags } = useFeatureFlags();
  const membershipFlowEnabled =
    marketHasMembership(market) && (featureFlags?.["membership_v2"] ?? true);
  const membershipPlanActive =
    useMembershipPlanStore((s) => s.active) && membershipFlowEnabled;

  // Delayed sidebar visibility - let scroll complete before sliding
  // Sidebar should be hidden on product and confirmation steps
  const [delayedSidebarVisible, setDelayedSidebarVisible] = useState(
    bookingFlow.currentStep !== "product" &&
      bookingFlow.currentStep !== "confirmation",
  );
  const prevStepRef = useRef(bookingFlow.currentStep);

  // Sync booking steps with browser history (back/forward buttons)
  useStepHistory();

  // Adapter methods for backward compatibility with BookingSteps
  const handleProductSelect = (
    productIds: string[],
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    componentMap?: Record<string, any>,
  ) => {
    const skipAddons =
      componentMap && "skipAddons" in componentMap && componentMap.skipAddons;
    bookingFlow.selectProducts(productIds, {
      skipNavigation: false,
      skipAddons: skipAddons || false,
    });
  };

  const handleProductRemove = (productId: string) => {
    bookingFlow.removeProduct(productId);
  };

  const handleAddOnToggle = (addOnId: string) => {
    bookingFlow.toggleAddOn(addOnId);
  };

  const handleClinicSelect = () => {
    bookingFlow.goToStep("date");
  };

  const handleClinicChange = (clinicId: string) => {
    bookingFlow.selectClinic(clinicId);
  };

  const handleDateSelect = (date: Date | null | undefined) => {
    bookingFlow.selectDate(date || null);
  };

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const handleTimeSelect = (times: string[], options?: any) => {
    if (options) {
      bookingFlow.selectTimeSlot(times, {
        previousSlot: options.previousSlot,
        preference: options.preference,
        dateOverride: options.dateOverride,
      });
    } else {
      bookingFlow.selectTimeSlot(times, {
        preference: "first",
      });
    }
  };

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const handleDeleteTimeSlot = (slot: any) => {
    bookingFlow.deleteTimeSlot(slot);
  };

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const handleFormSubmit = async (data: any) => {
    await bookingFlow.submitBooking(data);
  };

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const setCurrentStep = (step: any) => {
    bookingFlow.goToStep(step, { skipValidation: true });
  };

  const setBookingId = (id: string) => {
    bookingFlow.setBookingId(id);
  };

  // Delay sidebar slide-in when leaving product step (let scroll complete first)
  // Hide sidebar on product and confirmation steps
  useEffect(() => {
    const prevStep = prevStepRef.current;
    const currentStep = bookingFlow.currentStep;
    const shouldShowSidebar =
      currentStep !== "product" && currentStep !== "confirmation";
    const wasOnProduct = prevStep === "product";

    if (wasOnProduct && shouldShowSidebar) {
      // Coming FROM product step - delay sidebar to let scroll complete
      const timer = setTimeout(() => {
        setDelayedSidebarVisible(true);
      }, 250);
      return () => clearTimeout(timer);
    } else {
      // All other cases - immediate
      setDelayedSidebarVisible(shouldShowSidebar);
    }

    prevStepRef.current = currentStep;
  }, [bookingFlow.currentStep]);

  // Use delayed state for sidebar visibility
  const showSidebar = delayedSidebarVisible;

  return (
    // overflow-x-clip: the add-ons page's full-bleed MRI band uses a
    // w-screen pseudo-element that must not create horizontal scroll.
    <div className="min-h-screen bg-gray-50 overflow-x-clip">
      {/* Embedded Stripe payment overlay (opened by the membership CTA). */}
      <EmbeddedPaymentModal />
      {/* Main content with right padding on desktop to account for floating sidebar */}
      <div
        className={`transition-[padding] duration-[400ms] ${showSidebar ? "xl:pr-[25%]" : ""}`}
      >
        <BookingHeader
          language={bookingFlow.language}
          setLanguage={bookingFlow.setLanguage}
          currentStep={bookingFlow.currentStep}
          onStepClick={setCurrentStep}
          onBack={bookingFlow.goBack}
        />

        <div className="container mx-auto px-4 md:px-8 py-2 lg:py-4">
          <BookingSteps
            currentStep={bookingFlow.currentStep}
            handleProductSelect={handleProductSelect}
            handleClinicSelect={handleClinicSelect}
            selectedProducts={bookingFlow.selectedProducts}
            selectedClinic={bookingFlow.selectedClinic}
            handleClinicChange={handleClinicChange}
            handleProductRemove={handleProductRemove}
            handleBack={bookingFlow.goBack}
            canGoBack={bookingFlow.canGoBack}
            language={bookingFlow.language}
            currentDate={bookingFlow.selectedDate}
            handleDateSelect={handleDateSelect}
            selectedTimeSlots={bookingFlow.selectedTimeSlots}
            bookingType={bookingFlow.bookingType}
            setBookingType={bookingFlow.setBookingType}
            handleTimeSelect={handleTimeSelect}
            handleDeleteTimeSlot={handleDeleteTimeSlot}
            setCurrentStep={setCurrentStep}
            handleFormSubmit={handleFormSubmit}
            formData={bookingFlow.formData}
            bookingId={bookingFlow.bookingId}
            setBookingId={setBookingId}
            selectedAddOns={bookingFlow.selectedAddOns}
            handleAddOnToggle={handleAddOnToggle}
            viewMode={bookingFlow.viewMode}
            setViewMode={bookingFlow.setViewMode}
            selectedPackageType={bookingFlow.selectedPackageType}
          />
        </div>
      </div>

      <div>
        {/* FAQ Section - with right padding for floating sidebar */}
        {/* On the membership cadence page, push the FAQ further down so it
          clears the focused cadence content above. */}
        <div
          className={`transition-[padding] duration-[400ms] ${showSidebar ? "xl:pr-[25%]" : ""} ${membershipPlanActive ? "mt-2 lg:mt-4" : ""}`}
        >
          <div className="container mx-auto px-4 md:px-8">
            <FAQ
              language={bookingFlow.language}
              packageType={bookingFlow.selectedPackageType}
              membership={membershipPlanActive}
            />
          </div>
        </div>

        {/* Advisory Board Section - experts */}
        {marketHasAdvisoryBoard(market) && (
          <div
            className={`transition-[padding] duration-[400ms] ${showSidebar ? "xl:pr-[25%]" : ""}`}
          >
            <div className="container mx-auto px-4 md:px-8">
              <AdvisoryBoard language={bookingFlow.language} />
            </div>
          </div>
        )}

        <div
          className={`pt-6 pb-24 transition-[padding] duration-[400ms] ${showSidebar ? "xl:pr-[25%]" : ""}`}
          style={{
            paddingBottom: `calc(6rem + env(safe-area-inset-bottom))`,
          }}
        >
          <div className="container mx-auto px-4 md:px-8">
            <Footer language={bookingFlow.language} />
          </div>
        </div>
      </div>

      {/* Persistent Desktop Sidebar - renders at top level so it doesn't remount between steps */}
      <PersistentDesktopSidebar isVisible={showSidebar} />

      <SessionConflictDialog />
    </div>
  );
};

export default BookingCalendar;
